// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; the field captions are visual labels for the adjacent control, intentionally not wired via htmlFor (same convention as the UserAdmin* lists, Task #25).
//
// =============================================================================
// SupplierAdmin — the supplier admin screen (uniform-authorisation overlay)
// =============================================================================
// Admin-only editor (Manage group) for the `suppliers` table. The list stays
// here; New Supplier + Edit Supplier open the Unleashed-style SupplierFormModal
// (db-50 billing model + Zephyr overlay). Reads go through the src/data/suppliers
// repo; the modal owns the writes. Reference:
// docs/uniform-supplier-billing-model-plan.md.
// =============================================================================
import React from "react";
import { listSuppliers, SUPPLIERS_LIST_LIMIT } from "../../data/suppliers";
import { Btn } from "../../utils/ui.jsx";
import { SupplierFormModal } from "./SupplierFormModal.jsx";

const LIST_GRID = {
  gridTemplateColumns: "96px 1fr 48px 50px 56px 66px 80px 90px 120px 130px 66px",
};

// At-a-glance Yes/— cell for the list (so office staff can see which overlay fields
// are filled without opening each supplier).
function ynCell(present) {
  return present ? (
    <span className="text-[11px] font-bold text-[#2E7D32]">Yes</span>
  ) : (
    <span className="text-[12px] text-zephyr-muted">—</span>
  );
}

export function SupplierAdmin() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [uniformOnly, setUniformOnly] = React.useState(true);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [refreshTick, setRefreshTick] = React.useState(0);
  // null | { mode: "create" | "edit", row }
  const [modal, setModal] = React.useState(null);

  // Server-side filtering. PostgREST caps un-limited selects at 1,000 rows, so a
  // fetch-everything + client-filter approach silently lost the suppliers past
  // the first 1,000 alphabetically (UMS / Weareco). Instead re-query on each
  // filter change (debounced while typing); the repo applies the row limit.
  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      const t = setTimeout(
        function () {
          listSuppliers({ uniformOnly: uniformOnly, search: search }).then(function (data) {
            if (cancelled) return;
            if (data) setRows(data);
            setLoading(false);
          });
        },
        search ? 250 : 0
      );
      return function () {
        cancelled = true;
        clearTimeout(t);
      };
    },
    [uniformOnly, search, refreshTick]
  );

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  function onModalSaved() {
    const wasCreate = modal && modal.mode === "create";
    setRefreshTick(function (n) {
      return n + 1;
    });
    showSaved(wasCreate ? "Supplier created ✓" : "Saved ✓");
  }

  // Rows arrive already filtered + capped by the repo.
  const shown = rows;
  const capped = rows.length >= SUPPLIERS_LIST_LIMIT;

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="m-0 text-[22px] text-zephyr-text">Suppliers</h1>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
      </div>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        Suppliers are mirrored from Unleashed. Edit a supplier to set how it bills Zephyr, the
        client document, the Action Sheet next-steps, and the uniform Authorisation overlay.
      </p>

      <div className="flex gap-4 items-center mb-4">
        <input
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          placeholder="Search name or code…"
          className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-[280px]"
          style={{ fontFamily: "inherit" }}
        />
        <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer">
          <input
            type="checkbox"
            checked={uniformOnly}
            onChange={function (e) {
              setUniformOnly(e.target.checked);
            }}
          />
          Uniform suppliers only
        </label>
        <span className="text-[12px] text-zephyr-muted">
          {rows.length} supplier{rows.length === 1 ? "" : "s"}
          {capped ? ` — showing the first ${SUPPLIERS_LIST_LIMIT} matches, refine the search` : ""}
        </span>
        <Btn
          onClick={function () {
            setModal({ mode: "create", row: null });
          }}
          style={{ marginLeft: "auto" }}
        >
          + New supplier
        </Btn>
      </div>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading...</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-[10px] px-[18px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={LIST_GRID}
          >
            <div>Code</div>
            <div>Name</div>
            <div className="text-center">ABN</div>
            <div className="text-center">Logo</div>
            <div className="text-center">Terms</div>
            <div className="text-center">Acc No.</div>
            <div>Uniform</div>
            <div>SO Suffix</div>
            <div>Unleashed Code</div>
            <div>Doc Name</div>
            <div>Actions</div>
          </div>
          {shown.map(function (r, idx) {
            return (
              <div
                key={r.id}
                style={{
                  borderBottom: idx < shown.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: r.deactivated ? "#FFF3F3" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                }}
              >
                <div className="grid gap-0 py-[10px] px-[18px] items-center" style={LIST_GRID}>
                  <div>
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent border-0 p-0 font-semibold text-[13px] underline cursor-pointer text-left"
                      style={{
                        color: r.deactivated ? "#607D79" : "#1A2E2B",
                        fontFamily: "inherit",
                      }}
                    >
                      {r.supplier_code}
                    </button>
                    {r.deactivated && (
                      <span className="ml-[6px] text-[10px] font-bold text-white bg-[#C62828] rounded-[3px] py-[1px] px-[6px]">
                        DEACTIVATED
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-zephyr-text overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.name}
                  </div>
                  <div className="text-center">{ynCell(!!r.abn)}</div>
                  <div className="text-center">{ynCell(!!r.logo_path)}</div>
                  <div className="text-center">{ynCell(!!r.account_terms)}</div>
                  <div className="text-center">{ynCell(!!r.zephyr_account_number)}</div>
                  <div>
                    {r.is_uniform_supplier && (
                      <span className="text-[11px] font-bold text-white rounded px-2 py-[2px] bg-zephyr-teal">
                        UNIFORM
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] font-semibold text-[#00695C]">
                    {r.so_suffix || "—"}
                  </div>
                  <div
                    className="text-[12px] overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{
                      color:
                        r.is_uniform_supplier && !r.unleashed_product_code ? "#C62828" : "#5A6B68",
                      fontWeight: r.is_uniform_supplier && !r.unleashed_product_code ? 700 : 400,
                    }}
                  >
                    {r.unleashed_product_code || (r.is_uniform_supplier ? "MISSING" : "—")}
                  </div>
                  <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.doc_name || "—"}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border border-[#CFE0DE] text-[#1A2E2B]"
                      style={{ fontFamily: "inherit" }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {shown.length === 0 && (
            <div className="p-10 text-center text-zephyr-muted text-[13px]">
              No suppliers found.
            </div>
          )}
        </div>
      )}

      {modal && (
        <SupplierFormModal
          mode={modal.mode}
          row={modal.row}
          onClose={function () {
            setModal(null);
          }}
          onSaved={onModalSaved}
        />
      )}
    </div>
  );
}
